import type { ReactNode } from "react";

export type PageHeroArt = {
  /** public の下の本物の絵。描いた絵だけを使い、コードで図形を描かない */
  src: string;
  /** 暗い版の絵 (夜の色の組で描いた別のファイル)。渡すと、/luagate の明暗 (.lg の中の data-lg-mode と data-theme) で片方だけを見せる。.lg の外では昼の絵のまま。絵に明るさの filter を掛けると灰色にくすむため */
  darkSrc?: string;
  width: number;
  height: number;
};

export type PageHeroProps = {
  /** その頁で何ができるかを言い切る 1 文。配列で渡すと、1 つずつを折らない塊にして、塊の境目でだけ折る */
  headline: ReactNode | readonly ReactNode[];
  /** 細い字の行。2〜3 行 */
  lines?: readonly ReactNode[];
  /** 行の下に足す物 (出るときだけの 1 行) */
  extra?: ReactNode;
  /** 右の横長の絵。無い頁は渡さず、字だけの形にする (絵の場所を空けない) */
  art?: PageHeroArt;
  /** 読み上げの名前 */
  label: string;
};

/**
 * 頁の頭の、題の行 (PageHead) の下の段。左に大きな太字の 1 文と細い字の行、右に横長の絵。
 * 狭い欄 (760px 未満) では絵を上に、字を下に積む。絵の無い頁は字だけを読みやすい幅で置く
 */
export function PageHero({ headline, lines = [], extra, art, label }: PageHeroProps) {
  return (
    <section className={art ? "lg-pagehero" : "lg-pagehero lg-pagehero--text"} aria-label={label}>
      <div className="lg-pagehero__grid">
        <div className="lg-pagehero__text">
          <p className="lg-pagehero__headline">
            {Array.isArray(headline)
              ? (headline as readonly ReactNode[]).map((part, i) => (
                  <span key={i} className="lg-pagehero__headline-part">
                    {part}
                  </span>
                ))
              : headline}
          </p>
          {lines.length > 0 ? (
            <ul className="lg-pagehero__lines">
              {lines.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          ) : null}
          {extra}
        </div>
        {art ? (
          <div className="lg-pagehero__art">
            {/* 絵は飾り (中身は隣の字が言う)。頁の最初に見える絵なので遅らせない */}
            {art.darkSrc ? (
              <>
                {/* 明暗の 2 枚。どちらも lazy にし、CSS で隠した方は読み込まない (先に読む指定を付けると、隠した昼の絵も取りに行く) */}
                <img className="lg-pagehero__day" src={art.src} alt="" width={art.width} height={art.height} decoding="async" loading="lazy" />
                <img className="lg-pagehero__night" src={art.darkSrc} alt="" width={art.width} height={art.height} decoding="async" loading="lazy" />
              </>
            ) : (
              <img src={art.src} alt="" width={art.width} height={art.height} decoding="async" fetchPriority="high" />
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
}
